import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Home } from 'lucide-react';
import { useState, useEffect } from 'react';
import './Navbar.css';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  const isActive = (path) => location.pathname === path ? 'active' : '';

  useEffect(() => {
    // Force English as the default language for first-time visitors
    // This prevents the widget from auto-translating based on browser language
    if (!document.cookie.includes('googtrans=')) {
      document.cookie = "googtrans=/en/en; path=/;";
      document.cookie = `googtrans=/en/en; path=/; domain=${window.location.hostname}`;
      document.cookie = `googtrans=/en/en; path=/; domain=.${window.location.hostname}`;
    }

    // Add global callback if not present
    window.googleTranslateElementInit = () => {
      if (window.isGoogleTranslateInitialized) return;
      if (window.google && window.google.translate && typeof window.google.translate.TranslateElement === 'function') {
        window.isGoogleTranslateInitialized = true;
        try {
          new window.google.translate.TranslateElement({
            pageLanguage: 'en',
            includedLanguages: 'en,hi',
            autoDisplay: false
          }, 'google_translate_element');
          
          // Fix accessibility warning: Google Translate injects a <select> without an id/name
          const observer = new MutationObserver((mutations, obs) => {
            const selectElement = document.querySelector('.goog-te-combo');
            if (selectElement) {
              if (!selectElement.hasAttribute('id')) selectElement.setAttribute('id', 'google-translate-select');
              if (!selectElement.hasAttribute('name')) selectElement.setAttribute('name', 'google-translate-select');
              obs.disconnect(); // Stop observing once fixed
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
        } catch (e) {
          console.error("Google Translate initialization error:", e);
        }
      }
    };

    // Load the script if not already loaded
    if (!document.querySelector('script[src*="translate_a/element.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.defer = true;
      document.body.appendChild(script);
    } else {
      // If script is already there (due to hot reloading), just call the init
      if (window.googleTranslateElementInit) {
        window.googleTranslateElementInit();
      }
    }
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Olympiads', path: '/olympiads' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Results', path: '/results' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact Us', path: '/contact' },
  ];

  return (
    <header className="navbar-wrapper glass-panel">
      <div className="topbar">
        <div className="container topbar-container" style={{justifyContent: 'flex-end'}}>
          <div className="accessibility-controls">
            <div className="acc-group acc-home">
              <Link to="/" aria-label="Home"><Home size={16} /></Link>
            </div>
            <div className="acc-group acc-font">
              <button onClick={() => document.documentElement.style.fontSize = '90%'} className="font-btn" title="Decrease Font">A-</button>
              <button onClick={() => document.documentElement.style.fontSize = '100%'} className="font-btn" title="Default Font">A</button>
              <button onClick={() => document.documentElement.style.fontSize = '110%'} className="font-btn" title="Increase Font">A+</button>
            </div>
            <div className="acc-group acc-theme">
              <button onClick={() => document.documentElement.setAttribute('data-theme', 'light')} className="theme-btn light-theme-btn" title="Light Theme">T</button>
              <button onClick={() => document.documentElement.removeAttribute('data-theme')} className="theme-btn dark-theme-btn" title="Dark Theme">T</button>
            </div>
            <div className="acc-group acc-lang" style={{ position: 'relative' }}>
              <button 
                className="font-btn" 
                title="Toggle Hindi/English"
                style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '100%' }}
                onClick={() => {
                  const isHindi = document.cookie.includes('googtrans=/en/hi');
                  
                  if (isHindi) {
                    // Force English by setting it to /en/en instead of deleting, 
                    // which guarantees the widget won't default back to Hindi.
                    document.cookie = "googtrans=/en/en; path=/;";
                    document.cookie = `googtrans=/en/en; path=/; domain=${window.location.hostname}`;
                    document.cookie = `googtrans=/en/en; path=/; domain=.${window.location.hostname}`;
                    
                    // Also attempt to clear it just in case
                    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                  } else {
                    // Switch to Hindi
                    document.cookie = "googtrans=/en/hi; path=/;";
                    document.cookie = `googtrans=/en/hi; path=/; domain=${window.location.hostname}`;
                    document.cookie = `googtrans=/en/hi; path=/; domain=.${window.location.hostname}`;
                  }
                  window.location.reload();
                }}
              >
                <Globe size={16} />
                <span style={{ fontSize: '11px' }}>A/अ</span>
              </button>
              <div id="google_translate_element" style={{ opacity: 0, position: 'absolute', pointerEvents: 'none', width: '1px', height: '1px', overflow: 'hidden' }}></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container navbar-container">
        <Link to="/" className="navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <img src="/Dynamic Professional Skills.webp" alt="DPS Logo" style={{ height: '80px', objectFit: 'contain' }} />
          <img src="/Kediti Logo.webp" alt="Kedti Computer Institute" style={{ height: '60px', objectFit: 'contain' }} />
        </Link>

        {/* Desktop Menu */}
        <nav className="navbar-menu desktop-only">
          <ul className="nav-list">
            {navLinks.map((link) => (
              <li key={link.name}>
                <Link to={link.path} className={`nav-link ${isActive(link.path)}`}>
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Toggle */}
        <button className="mobile-toggle mobile-only" onClick={toggleMenu} aria-label="Toggle menu">
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu (Always in DOM for repaint-free sliding via CSS transforms) */}
      <div className={`mobile-menu glass-panel mobile-only ${isOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-list">
          {navLinks.map((link) => (
            <li key={link.name}>
              <Link 
                to={link.path} 
                className={`mobile-nav-link ${isActive(link.path)}`}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}
